import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { get, post, put, del } from '../../../api/client';
import { usePermission } from '../../../hooks/usePermission';
import { useOrderBehavior, formatRemaining } from '../../../hooks/useOrderBehavior';
import { handleApiError, showErrorNotification } from '../../../utils/errorHandler';
import { CollapsibleSection, Toggle, inputCls } from './shared';

interface ServiceType {
  id: string;
  name: string;
  createdBy: string | null;
  createdAt: string;
  /**
   * 这个名字在我名下还挂着多少张未软删订单（服务端 `order_count`，口径与订单查询同源）。
   * 只有服务类型目录带这一列；改名确认要用它说清影响面。
   */
  orderCount: number;
}

/* ========== 服务类型管理 ========== */
function ServiceTypesManager() {
  const queryClient = useQueryClient();
  const [adding, setAdding] = useState(false);
  const [addName, setAddName] = useState('');
  const [editId, setEditId] = useState<string | null>(null);
  const [editName, setEditName] = useState('');
  const [deleteId, setDeleteId] = useState<string | null>(null);

  // 服务端只回「系统预设 + 本人自建」两类，因此这里不需要再按 userId 过滤一遍
  const { data: types = [], isLoading: loading } = useQuery({
    queryKey: ['serviceTypes'],
    queryFn: async () => {
      const data = await get<ServiceType[]>('/service-types');
      return data ?? [];
    },
  });

  // created_by 由服务端按登录态注入：客户端上送它既没必要，也是给别人名下挂项的口子
  const fail = (action: string) => (error: unknown) => {
    console.error(`${action}:`, error);
    showErrorNotification(handleApiError(error));
  };

  const addServiceTypeMutation = useMutation({
    mutationFn: async (name: string) => post('/service-types', { name }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['serviceTypes'] });
      setAddName('');
      setAdding(false);
    },
    onError: fail('添加服务类型失败'),
  });

  const editServiceTypeMutation = useMutation({
    mutationFn: async ({ id, name }: { id: string; name: string }) => {
      return put(`/service-types/${id}`, { name });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['serviceTypes'] });
      setEditId(null);
    },
    onError: fail('编辑服务类型失败'),
  });

  const deleteServiceTypeMutation = useMutation({
    mutationFn: async (id: string) => {
      return del(`/service-types/${id}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['serviceTypes'] });
      setDeleteId(null);
    },
    onError: fail('删除服务类型失败'),
  });

  const handleAdd = () => {
    if (!addName.trim()) return;
    addServiceTypeMutation.mutate(addName.trim());
  };

  const handleEdit = (id: string) => {
    if (!editName.trim()) return;
    editServiceTypeMutation.mutate({ id, name: editName.trim() });
  };

  const handleDelete = (id: string) => {
    deleteServiceTypeMutation.mutate(id);
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-3">
        <div>
          <p className="text-sm font-semibold text-gray-800">服务类型</p>
          <p className="text-xs text-gray-400 mt-0.5">
            管理订单可选的服务类型，用于区分不同业务场景。自建项仅本人可见可改，带「系统预设」标记的由管理员维护、此处只读。
          </p>
        </div>
        <button type="button" onClick={() => setAdding(v => !v)}
          className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium bg-teal-500 hover:bg-teal-600 text-white rounded-lg transition-colors cursor-pointer whitespace-nowrap">
          <div className="w-3 h-3 flex items-center justify-center"><i className="ri-add-line" /></div>
          新增服务类型
        </button>
      </div>

      {adding && (
        <div className="mb-3 p-3 bg-teal-50 border border-teal-100 rounded-lg space-y-2">
          <input value={addName} onChange={e => setAddName(e.target.value)} placeholder="服务类型名称（必填）" className={inputCls} />
          <div className="flex gap-2 justify-end">
            <button type="button" onClick={() => { setAdding(false); setAddName(''); }}
              className="px-3 py-1.5 text-xs text-gray-500 border border-gray-200 bg-white rounded-lg cursor-pointer whitespace-nowrap hover:bg-gray-50">取消</button>
            <button type="button" onClick={handleAdd}
              className="px-3 py-1.5 text-xs text-white bg-teal-500 hover:bg-teal-600 rounded-lg cursor-pointer whitespace-nowrap">确认添加</button>
          </div>
        </div>
      )}

      {loading ? (
        <div className="py-6 text-center text-sm text-gray-400">加载中...</div>
      ) : types.length === 0 ? (
        <div className="py-6 text-center text-sm text-gray-400 bg-gray-50 rounded-lg">暂无服务类型，点击右上角新增</div>
      ) : (
        <div className="border border-gray-100 rounded-lg overflow-hidden">
          {types.map((t, idx) => (
            <div key={t.id} className={`${idx !== 0 ? 'border-t border-gray-100' : ''}`}>
              {editId === t.id ? (
                <div className="p-3 bg-teal-50 space-y-2">
                  <input value={editName} onChange={e => setEditName(e.target.value)} className={inputCls} />
                  {t.orderCount > 0 && editName.trim() !== t.name && (
                    <p className="text-xs text-amber-600">
                      「{t.name}」还挂在 {t.orderCount} 张历史订单上，改名只影响新建订单，这些单子仍显示旧名。
                    </p>
                  )}
                  <div className="flex gap-2 justify-end">
                    <button type="button" onClick={() => setEditId(null)}
                      className="px-3 py-1.5 text-xs text-gray-500 border border-gray-200 bg-white rounded-lg cursor-pointer whitespace-nowrap">取消</button>
                    <button type="button" onClick={() => handleEdit(t.id)}
                      className="px-3 py-1.5 text-xs text-white bg-teal-500 hover:bg-teal-600 rounded-lg cursor-pointer whitespace-nowrap">保存</button>
                  </div>
                </div>
              ) : (
                <div className="flex items-center justify-between px-4 py-3 hover:bg-gray-50 group">
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 flex items-center justify-center bg-teal-50 rounded-lg shrink-0">
                      <i className="ri-service-line text-teal-500 text-sm" />
                    </div>
                    <div className="flex items-center gap-2">
                      <p className="text-sm font-medium text-gray-800">{t.name}</p>
                      {t.createdBy === null && (
                        <span className="flex items-center gap-1 px-1.5 py-0.5 bg-gray-100 text-gray-400 text-xs rounded">
                          <i className="ri-lock-line text-xs" />
                          系统预设
                        </span>
                      )}
                    </div>
                  </div>
                  {t.createdBy !== null && (
                    <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                      <button type="button" onClick={() => { setEditId(t.id); setEditName(t.name); }}
                        className="w-7 h-7 flex items-center justify-center text-gray-400 hover:text-teal-500 hover:bg-teal-50 rounded-md transition-colors cursor-pointer">
                        <i className="ri-pencil-line text-sm" />
                      </button>
                      {deleteId === t.id ? (
                        <div className="flex items-center gap-1">
                          <button type="button" onClick={() => setDeleteId(null)} className="px-2 py-1 text-xs text-gray-500 border border-gray-200 rounded cursor-pointer whitespace-nowrap">取消</button>
                          <button type="button" onClick={() => handleDelete(t.id)} className="px-2 py-1 text-xs text-white bg-red-400 hover:bg-red-500 rounded cursor-pointer whitespace-nowrap">确认删除</button>
                        </div>
                      ) : (
                        <button type="button" onClick={() => setDeleteId(t.id)}
                          className="w-7 h-7 flex items-center justify-center text-gray-400 hover:text-red-400 hover:bg-red-50 rounded-md transition-colors cursor-pointer">
                          <i className="ri-delete-bin-line text-sm" />
                        </button>
                      )}
                    </div>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/* ========== 订单分类管理 ========== */
interface OrderCategory {
  id: string;
  name: string;
  createdBy: string | null;
  createdAt: string;
}

function OrderCategoriesManager() {
  const { isAdmin } = usePermission();
  const queryClient = useQueryClient();
  const [adding, setAdding] = useState(false);
  const [addName, setAddName] = useState('');
  const [editId, setEditId] = useState<string | null>(null);
  const [editName, setEditName] = useState('');
  const [deleteId, setDeleteId] = useState<string | null>(null);

  // 订单分类是全局目录：所有人可读，只有管理员可写（后端 AdminGuard 兜底）
  const { data: cats = [], isLoading: loading } = useQuery({
    queryKey: ['orderCategories'],
    queryFn: async () => {
      const data = await get<OrderCategory[]>('/order-categories');
      return data ?? [];
    },
  });

  const fail = (action: string) => (error: unknown) => {
    console.error(`${action}:`, error);
    showErrorNotification(handleApiError(error));
  };

  const addOrderCategoryMutation = useMutation({
    mutationFn: async (name: string) => {
      return post('/order-categories', { name });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['orderCategories'] });
      setAddName('');
      setAdding(false);
    },
    onError: fail('添加订单分类失败'),
  });

  const editOrderCategoryMutation = useMutation({
    mutationFn: async ({ id, name }: { id: string; name: string }) => {
      return put(`/order-categories/${id}`, { name });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['orderCategories'] });
      setEditId(null);
    },
    onError: fail('编辑订单分类失败'),
  });

  const deleteOrderCategoryMutation = useMutation({
    mutationFn: async (id: string) => {
      return del(`/order-categories/${id}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['orderCategories'] });
      setDeleteId(null);
    },
    onError: fail('删除订单分类失败'),
  });

  const handleAdd = () => {
    if (!addName.trim()) return;
    addOrderCategoryMutation.mutate(addName.trim());
  };

  const handleEdit = (id: string) => {
    if (!editName.trim()) return;
    editOrderCategoryMutation.mutate({ id, name: editName.trim() });
  };

  const handleDelete = (id: string) => {
    deleteOrderCategoryMutation.mutate(id);
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-3">
        <div>
          <p className="text-sm font-semibold text-gray-800">订单分类</p>
          <p className="text-xs text-gray-400 mt-0.5">
            {isAdmin
              ? '全平台共用的一套：名称含「支出」即按支出记账，「收入订单」「支出订单」为系统内置，参与收支方向与对方视角判定，不可改名或删除'
              : '查看可选订单分类，仅管理员可新增 / 编辑 / 删除'}
          </p>
        </div>
        {isAdmin && (
          <button type="button" onClick={() => setAdding(v => !v)}
            className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium bg-teal-500 hover:bg-teal-600 text-white rounded-lg transition-colors cursor-pointer whitespace-nowrap">
            <div className="w-3 h-3 flex items-center justify-center"><i className="ri-add-line" /></div>
            新增分类
          </button>
        )}
      </div>

      {adding && isAdmin && (
        <div className="mb-3 p-3 bg-teal-50 border border-teal-100 rounded-lg space-y-2">
          <input value={addName} onChange={e => setAddName(e.target.value)} placeholder="分类名称（必填）" className={inputCls} />
          <div className="flex gap-2 justify-end">
            <button type="button" onClick={() => { setAdding(false); setAddName(''); }}
              className="px-3 py-1.5 text-xs text-gray-500 border border-gray-200 bg-white rounded-lg cursor-pointer whitespace-nowrap hover:bg-gray-50">取消</button>
            <button type="button" onClick={handleAdd}
              className="px-3 py-1.5 text-xs text-white bg-teal-500 hover:bg-teal-600 rounded-lg cursor-pointer whitespace-nowrap">确认添加</button>
          </div>
        </div>
      )}

      {loading ? (
        <div className="py-6 text-center text-sm text-gray-400">加载中...</div>
      ) : cats.length === 0 ? (
        <div className="py-6 text-center text-sm text-gray-400 bg-gray-50 rounded-lg">暂无订单分类，点击右上角新增</div>
      ) : (
        <div className="border border-gray-100 rounded-lg overflow-hidden">
          {cats.map((cat, idx) => (
            <div key={cat.id} className={`${idx !== 0 ? 'border-t border-gray-100' : ''}`}>
              {editId === cat.id ? (
                <div className="p-3 bg-teal-50 space-y-2">
                  <input value={editName} onChange={e => setEditName(e.target.value)} className={inputCls} />
                  <div className="flex gap-2 justify-end">
                    <button type="button" onClick={() => setEditId(null)}
                      className="px-3 py-1.5 text-xs text-gray-500 border border-gray-200 bg-white rounded-lg cursor-pointer whitespace-nowrap">取消</button>
                    <button type="button" onClick={() => handleEdit(cat.id)}
                      className="px-3 py-1.5 text-xs text-white bg-teal-500 hover:bg-teal-600 rounded-lg cursor-pointer whitespace-nowrap">保存</button>
                  </div>
                </div>
              ) : (
                <div className="flex items-center justify-between px-4 py-3 hover:bg-gray-50 group">
                  <div className="flex items-center gap-3">
                    <div className={`w-8 h-8 flex items-center justify-center rounded-lg shrink-0 ${cat.name.includes('收入') ? 'bg-emerald-50' : cat.name.includes('支出') ? 'bg-rose-50' : 'bg-teal-50'}`}>
                      <i className={`text-sm ${cat.name.includes('收入') ? 'ri-arrow-down-circle-line text-emerald-500' : cat.name.includes('支出') ? 'ri-arrow-up-circle-line text-rose-400' : 'ri-price-tag-3-line text-teal-500'}`} />
                    </div>
                    <div>
                      <div className="flex items-center gap-2">
                        <p className="text-sm font-medium text-gray-800">{cat.name}</p>
                        {cat.createdBy === null && (
                          <span className="flex items-center gap-1 px-1.5 py-0.5 bg-gray-100 text-gray-400 text-xs rounded">
                            <i className="ri-lock-line text-xs" />
                            系统预设
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                  {isAdmin && cat.createdBy !== null && (
                    <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                      <button type="button" onClick={() => { setEditId(cat.id); setEditName(cat.name); }}
                        className="w-7 h-7 flex items-center justify-center text-gray-400 hover:text-teal-500 hover:bg-teal-50 rounded-md transition-colors cursor-pointer">
                        <i className="ri-pencil-line text-sm" />
                      </button>
                      {deleteId === cat.id ? (
                        <div className="flex items-center gap-1">
                          <button type="button" onClick={() => setDeleteId(null)} className="px-2 py-1 text-xs text-gray-500 border border-gray-200 rounded cursor-pointer whitespace-nowrap">取消</button>
                          <button type="button" onClick={() => handleDelete(cat.id)} className="px-2 py-1 text-xs text-white bg-red-400 hover:bg-red-500 rounded cursor-pointer whitespace-nowrap">确认删除</button>
                        </div>
                      ) : (
                        <button type="button" onClick={() => setDeleteId(cat.id)}
                          className="w-7 h-7 flex items-center justify-center text-gray-400 hover:text-red-400 hover:bg-red-50 rounded-md transition-colors cursor-pointer">
                          <i className="ri-delete-bin-line text-sm" />
                        </button>
                      )}
                    </div>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/* ========== 订单行为设置 ========== */
function OrderBehaviorSettings() {
  const { behavior, loading, switching, setEnabled, switchError } = useOrderBehavior();
  const { allowPastDates, remainingMs, lockedByAdmin, windowHours } = behavior;

  const toggleAllowPastDates = () => {
    setEnabled(!allowPastDates);
  };

  return (
    <div className="border border-gray-100 rounded-lg overflow-hidden">
      <div className="flex items-start justify-between px-4 py-4">
        <div className="flex items-start gap-3">
          <div className="w-8 h-8 flex items-center justify-center bg-orange-50 rounded-lg mt-0.5 shrink-0">
            <i className="ri-calendar-check-line text-orange-400 text-base" />
          </div>
          <div>
            <p className="text-sm font-medium text-gray-800">允许选择过去日期（补录模式）</p>
            <p className="text-xs text-gray-400 mt-0.5 leading-relaxed max-w-sm">
              正常订单均为未来日期的预约。开启后可补录历史订单，过往订单详情页将标记为“补录”。
              <span className="block mt-1 text-orange-400 font-medium">
                开启后将在 {windowHours} 小时后自动关闭，防止遗忘关闭造成误录
              </span>
              <span className="block mt-1">该开关跟随账号，四端共用同一份状态。</span>
            </p>
            <div className="mt-2">
              {loading ? (
                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-500">
                  <span className="w-1.5 h-1.5 rounded-full bg-gray-400" />
                  读取中...
                </span>
              ) : lockedByAdmin ? (
                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-rose-50 text-rose-500">
                  <i className="ri-lock-line text-xs" />
                  平台已统一关闭补录，如需补录历史订单请联系管理员
                </span>
              ) : allowPastDates ? (
                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-orange-50 text-orange-500">
                  <span className="w-1.5 h-1.5 rounded-full bg-orange-400 animate-pulse" />
                  补录模式进行中 · 剩余 {formatRemaining(remainingMs)}
                </span>
              ) : (
                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-500">
                  <span className="w-1.5 h-1.5 rounded-full bg-gray-400" />
                  已关闭（仅限今天及未来）
                </span>
              )}
            </div>
            {switchError && (
              <p className="mt-2 text-xs text-rose-500">
                {handleApiError(switchError).message}
              </p>
            )}
          </div>
        </div>
        <Toggle checked={allowPastDates} disabled={loading || switching || lockedByAdmin} onChange={toggleAllowPastDates} />
      </div>
    </div>
  );
}

/* ========== 导出主面板 ========== */
export default function OrderSettingsPanel() {
  return (
    <div className="space-y-3 md:space-y-8">
      <CollapsibleSection title="服务类型">
        <ServiceTypesManager />
      </CollapsibleSection>
      <CollapsibleSection title="订单分类">
        <OrderCategoriesManager />
      </CollapsibleSection>
      <CollapsibleSection title="预约日期行为" defaultOpen={false}>
        <OrderBehaviorSettings />
      </CollapsibleSection>
    </div>
  );
}
